import { Box, Button, CloseButton, Divider, Flex, FormControl, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Progress, Text, Tooltip, VStack } from "@chakra-ui/react";
import { keyframes } from '@emotion/react';
import { useToastr } from "@laxmimanogna/code-quick-components";
import { useContext, useEffect, useRef, useState } from "react";
import { AccountContext } from "../../../providers/AccountsProvider";
import { successToast } from "../../../components/successToast";
import AppColors from "../../../constants/AppColors";
import { ArrowDownIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import { AlertTriangle, CheckCircle, Loader2, XCircle } from "lucide-react";

const UploadProviderCSVForm = ({
  isOpen,
  onClose,
  departmentId,
  setListProviderData,
}) => {
  const toast = useToastr();
  const [csvFile, setCSVFile] = useState(null);
  const accountsContext = useContext(AccountContext);
  const [uploadStatus, setUploadStatus] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const scrollRef = useRef(null);

  const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [uploadStatus]);

  const parseBoolean = (value) => {
    return String(value).trim().toLowerCase() === 'true';
  };

  const handleFileChange = (e) => {
    setCSVFile(e.target.files[0]);
    setUploadStatus([]);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Success':
        return { icon: <CheckCircle size={16} color="#38A169" />, color: 'green.600' };
      case 'Duplicate':
        return { icon: <AlertTriangle size={16} color="#DD6B20" />, color: 'orange.500' };
      case 'Failed':
        return { icon: <XCircle size={16} color="#E53E3E" />, color: 'red.500' };
      default:
        return {
          icon: (
            <Box as="span" animation={`${spin} 1s linear infinite`} display="inline-block">
              <Loader2 size={16} color={AppColors.secondary} />
            </Box>
          ),
          color: AppColors.secondary,
        };
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ['Email', 'FirstName', 'LastName', 'can_upload', 'createMoodleAccount'];
    const instructions = ['example@email.com', 'John', 'Doe', 'true', 'false']; // Sample values

    const csvContent = [headers.join(','), instructions.join(',')].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'provider_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!csvFile) {
      toast.showError({ description: 'Please upload a CSV file' });
      return;
    }

    try {
      setIsUploading(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const rows = text.split('\n').slice(1);
        const statusUpdates = [];

        for (let row of rows) {
          if (!row.trim()) continue;
          const [email, first_name, last_name, can_upload, createMoodleAccount] = row.split(',').map(v => v.trim());
          const shouldCreateMoodleAccount = parseBoolean(createMoodleAccount);

          const name = `${first_name} ${last_name}`;
          statusUpdates.push({ email, name, status: 'Processing' });
          setUploadStatus([...statusUpdates]);

          const isEmailExist = accountsContext.providerData?.find(lp => lp?.email === email);
          if (isEmailExist) {
            statusUpdates[statusUpdates.length - 1].status = 'Duplicate';
            setUploadStatus([...statusUpdates]);
            continue;
          }

          try {
            const providerBody = {
              email,
              first_name,
              last_name,
              can_upload: parseBoolean(can_upload),
              // createMoodleAccount: parseBoolean(createMoodleAccount),
            };

            const res = await accountsContext.createProvider(departmentId, providerBody);
            if (shouldCreateMoodleAccount) {
              await accountsContext.moodleAccountCreate(providerBody);
            }
            if (res) {
              setListProviderData(prev => [...(prev || []), res]);
              statusUpdates[statusUpdates.length - 1].status = 'Success';
            } else {
              statusUpdates[statusUpdates.length - 1].status = 'Failed';
            }
          } catch (err) {
            statusUpdates[statusUpdates.length - 1].status = 'Failed';
          }

          setUploadStatus([...statusUpdates]);
        }

        const toastProps = {
          header: 'Success!',
          description: `CSV upload complete`,
        };
        successToast(toast, toastProps);
        setCSVFile(null);
        setIsUploading(false);
        // onClose();
      };
      reader.readAsText(csvFile);
    } catch (err) {
      toast.showError({ description: `Upload failed: ${err.message}` });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent mx={4} px={{ base: 4, md: 8 }} py={6}>
        {isUploading && (
          <Progress
            size="xs"
            isIndeterminate
            colorScheme="blue"
            borderTopRadius="md"
            position="absolute"
            top={0}
            left={0}
            right={0}
          />
        )}
        <ModalHeader textAlign="center">Create Providers via CSV</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6}>
            <FormControl textAlign="center">
              <Box
                border="2px dashed"
                borderColor={isDragOver ? AppColors.primary : "gray.300"}
                bg={isDragOver ? "gray.50" : "transparent"}
                borderRadius="md"
                p={6}
                cursor="pointer"
                transition="0.2s"
                textAlign="center"
                position="relative"
                onClick={() => document.getElementById('csvFileInput').click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'copy';
                  setIsDragOver(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragOver(false);

                  const droppedFile = e.dataTransfer.files[0];
                  if (droppedFile && droppedFile.type === 'text/csv') {
                    setCSVFile(droppedFile);
                  } else {
                    toast.showError({ description: 'Only CSV files are supported' });
                  }
                }}
                _hover={{ borderColor: AppColors.primary, bg: 'gray.50' }}
              >
                <Input
                  id="csvFileInput"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  display="none"
                />

                {csvFile && (
                  <CloseButton
                    size="sm"
                    position="absolute"
                    top="6px"
                    right="6px"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent triggering file input
                      setCSVFile(null);
                    }}
                    color="gray.500"
                    _hover={{ color: "red.500" }}
                  />
                )}

                {csvFile ? (
                  <>
                    <Text fontWeight="medium" color="green.600" fontSize="sm">
                      {csvFile.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      File ready to upload
                    </Text>
                  </>
                ) : (
                  <>
                    <ArrowDownIcon boxSize={6} color="gray.500" mb={2} />
                    <Text fontSize="sm" color="gray.600">
                      Drop or click to select file
                    </Text>
                    <Text mt={2} fontSize="xs" color="gray.500">
                      Only .csv files are supported.
                    </Text>
                  </>
                )}
              </Box>
            </FormControl>

            {uploadStatus.length > 0 && (
              <>
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={4}
                  pb={2}
                  mt={2}
                  maxH="270px"
                  overflowY="auto"
                  w="full"
                >
                  <Box
                    position="sticky"
                    top="0"
                    zIndex="1"
                    bg="white"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    h="35px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                      Upload Status
                    </Text>
                  </Box>
                  <VStack spacing={2} align="start" w="full">
                    {uploadStatus.map(({ email, name, status }, idx) => {
                      const { icon, color } = getStatusIcon(status);
                      return (
                        <Flex key={idx} justify="space-between" align="center" w="full" px={2} py={1} borderRadius="md" _hover={{ bg: 'gray.50' }}>
                          <HStack spacing={3} maxW="80%">
                            <Text fontSize="xs" fontWeight="bold" color="gray.600">{idx + 1}.</Text>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium">{name}</Text>
                              <Text fontSize="xs" color="gray.500">{email}</Text>
                            </Box>
                          </HStack>
                          <HStack spacing={1}>
                            {icon}
                            <Text fontSize="xs" fontWeight="medium" color={color}>{status}</Text>
                          </HStack>
                        </Flex>
                      );
                    })}
                    <Box ref={scrollRef} />
                  </VStack>
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter flexDirection="column" gap={3}>
          {csvFile ? (
            <HStack justify="center">
              <Button colorScheme="green" onClick={handleUpload}>
                Create
              </Button>
            </HStack>
          ) : (
            <>
              <Divider my={4} />
              <Flex justify="center" align="center" mt={2} gap={3}>
                <Tooltip
                  label="Email, FirstName, LastName, can_upload, createMoodleAccount"
                  fontSize="xs"
                  bg="gray.100"
                  color="gray.900"
                  borderRadius="md"
                  px={3}
                  py={2}
                  whiteSpace="nowrap"
                  maxW="none"
                  hasArrow
                >
                  <Flex align="center" cursor="pointer">
                    <InfoOutlineIcon boxSize={3} color="gray.500" mr={1} />
                    <Text fontSize="xs" color="gray.500">
                      Required format
                    </Text>
                  </Flex>
                </Tooltip>

                <Divider orientation="vertical" h="12px" borderColor="gray.300" />

                <Button
                  variant="link"
                  color={AppColors.secondary}
                  fontSize="xs"
                  onClick={handleDownloadTemplate}
                  _hover={{ textDecoration: 'underline' }}
                  p={0}
                  h="auto"
                  minW="unset"
                >
                  Download CSV Template
                </Button>
              </Flex>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
export default UploadProviderCSVForm;
